import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useContext, useMemo } from 'react';
import { Author, HeatingCircuit, } from 'z3k-config';
import { useEditingProps, useEditItem, useGetZ3kObject } from '../utils/unpack-props';
import { MaterialDropdown, MaterialObjectSelection, StrictNumberInput, TempstepField, } from 'console/shared/components/fields';
import { ErrorBoundary } from 'shared/components/error-boundary';
import { InputAdornment } from '@mui/material';
import { useBox } from 'shared/hooks/utils';
import { HeatRequestField } from './heat-request';
import { HCAdditionalParams } from './additional-params/additional-main';
import { SettingFullRow } from 'console/device-settings/z3k/components/material-utils/settings-card/full-row';
import { SettingContainer } from 'console/device-settings/z3k/components/material-utils/settings-card/container';
import { SettingCard } from 'console/device-settings/z3k/components/material-utils/settings-card/card';
import { analogOutputs, boilerAdaptersSelector, boilerCircuitBoilerGroupOptionsSelector, circuit_type_options_selector, wired_radio_boiler_adapter_options_selector, wired_radio_options_selector, } from 'z3k-parser/lib/z3k-selectors';
import { selectBoilerAdaptersOptions, selectPumpsOptions, selectRelayControlsOptions, selectThreewayTapsOptions, } from 'console/device-settings/z3k/components/utils/z3k-selectors';
import { HeatingCircuitContext } from 'console/device-settings/z3k/components/heating-circuits/context/type';
import { Z3KSettings, Z3KSettingsEditor, } from 'console/device-settings/z3k/provider/types';
import { isAuthor } from 'z3k-parser/model/author';
import { MultipleSelectionTable, } from '../material-utils/multiple-selection-table/multiple-selection';
import { IconsLibrary } from 'console/shared/components/fields/icon';
import { heating_circuits_icons } from 'console/shared/components/fields/icon/icons';
import { removeNulls } from 'utils';
import { Control } from 'console/shared/components/fields/control';
import { ControlWithoutValidation } from 'console/shared/components/fields/control/without-validation';
import { TextInputWrapper } from 'console/shared/components/fields/text';
import { ControlWaterTemperature } from 'z3k-settings/components/heating-circuits/control-water-temperature';
import { FlagSelector } from 'z3k-settings/components/material-utils/flag-selector';
import { CircuitSwitch } from 'z3k-settings/components/heating-circuits/circuit-switch';
const consumer_mode_options = [
    {
        text: 'по воздуху',
        value: HeatingCircuit.ConsumerMode.AIR,
    },
    {
        text: 'по воздуху с ПИД-регулятором теплоносителя',
        value: HeatingCircuit.ConsumerMode.AIR_PID,
    },
    {
        text: 'по теплоносителю',
        value: HeatingCircuit.ConsumerMode.WATER,
    },
];
const z3k_name = 'heating_circuits';
export const HeatingCircuitComponent = memo(function HeatingCircuitControl() {
    const item = useGetZ3kObject(z3k_name);
    const id = item.id;
    const onChange = useEditItem(id, z3k_name);
    const { replace_device_ui_settings, config } = useContext(Z3KSettingsEditor);
    const { device_spec, service_view, device_ui_settings, deviceFirmwareVersion, is_staff, } = useContext(Z3KSettings);
    const boiler_adapter_ids = boilerAdaptersSelector(config);
    const boilerAdapters = selectBoilerAdaptersOptions(config);
    const relays = selectRelayControlsOptions(config);
    const pumps = selectPumpsOptions(config);
    const wired_temperature_radiosensors_options = wired_radio_options_selector(config);
    const wired_radio_boiler_options = wired_radio_boiler_adapter_options_selector(config);
    const boiler_circuit_boiler_group_options = boilerCircuitBoilerGroupOptionsSelector(config);
    const circuit_type_options = circuit_type_options_selector(config);
    const threewayTaps = selectThreewayTapsOptions(config);
    const analog_outputs = analogOutputs(config);
    const hcBox = useBox(item);
    const onChangeType = useCallback((type) => onChange(HeatingCircuit.changeHcType(boiler_adapter_ids, hcBox.current, type)), [onChange, boiler_adapter_ids]);
    const withBoilerAdapter = useMemo(() => HeatingCircuit.isCircuitWithBoilerAdapter(boiler_adapter_ids, item), [boiler_adapter_ids, item]);
    const isFlowType = useMemo(() => HeatingCircuit.isFlowDHW(item), [item.setting_register]);
    const nameEditingProps = useEditingProps(id, z3k_name, 'name');
    const typeEditingProps = useEditingProps(id, z3k_name, 'type');
    const hysteresisEditingProps = useEditingProps(id, z3k_name, 'hysteresis');
    const turnOffDelayEditingProps = useEditingProps(id, z3k_name, 'turn_off_delay');
    const heatSourceEditingProps = useEditingProps(id, z3k_name, 'heat_source');
    const heatRequestEditingProps = useEditingProps(id, z3k_name, 'heat_request');
    const airTempSensorEditingProps = useEditingProps(id, z3k_name, 'air_temp_sensor');
    const airTempSensorReserveEditingProps = useEditingProps(id, z3k_name, 'air_temp_sensor_reserve');
    const dhwTempSensorEditingProps = useEditingProps(id, z3k_name, 'dhw_temp_sensor');
    const dhwTempSensorReserveEditingProps = useEditingProps(id, z3k_name, 'dhw_temp_sensor_reserve');
    const controlHysteresis = service_view && (_jsx(Control, { label: "\u0413\u0438\u0441\u0442\u0435\u0440\u0435\u0437\u0438\u0441 \u0440\u0435\u0433\u0443\u043B\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F", field: TempstepField, help_id: "heating_circuits_hysteresis", ...hysteresisEditingProps }));
    // https://git.microline.ru/zont/server/-/issues/1290
    const external_thermostat_in_lock_mode = useMemo(() => item.external_thermostat != null
        ? HeatingCircuit.isExtThermostatLockMode(item)
        : true, [item.external_thermostat, item.setting_register]);
    const off_delay_field = useCallback((input_props) => (_jsx(StrictNumberInput, { ...input_props, min: 0, max: 600, endAdornment: _jsx(InputAdornment, { position: "end", children: "\u0441\u0435\u043A" }) })), []);
    const controlTurnOffDelay = service_view &&
        device_spec.heating.has_heating &&
        device_spec.heating.has_extended_heating.show_turn_off_delay &&
        !(withBoilerAdapter && isFlowType) && (_jsx(Control, { label: "\u0417\u0430\u0434\u0435\u0440\u0436\u043A\u0430 \u0432\u044B\u043A\u043B\u044E\u0447\u0435\u043D\u0438\u044F \u043D\u0430\u0433\u0440\u0435\u0432\u0430", field: off_delay_field, help_id: "heating_circuits_turn_off_delay", ...turnOffDelayEditingProps }));
    const isDisabledType = useMemo(() => isAuthor(item.id, Author.PREDEFINED), [item.id]);
    const type_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: circuit_type_options, disabled: !service_view || isDisabledType })), [device_spec, circuit_type_options, service_view, isDisabledType]);
    const consumer_mode_field = useCallback((input_props) => (_jsx(MaterialDropdown, { ...input_props, options: consumer_mode_options, disabled: !service_view })), [consumer_mode_options, service_view]);
    const onChangeConsumerMode = useCallback((new_mode) => {
        let new_hc = HeatingCircuit.setConsumerMode(hcBox.current, new_mode);
        if (HeatingCircuit.isWaterConsumerMode(new_hc)) {
            new_hc = {
                ...new_hc,
                air_temp_sensor: null,
                air_temp_sensor_reserve: null,
            };
        }
        // для чего это? -> https://git.microline.ru/zont/server/-/issues/578
        if (HeatingCircuit.isAirPIDConsumerMode(new_hc) ||
            HeatingCircuit.isWaterConsumerMode(new_hc)) {
            new_hc = HeatingCircuit.setAlwaysHeatRequest(new_hc, false);
        }
        onChange(new_hc);
    }, [onChange]);
    const onChangeControlDevices = useCallback((new_control_devices) => {
        const wasBoilerAdapter = HeatingCircuit.isCircuitWithBoilerAdapter(boiler_adapter_ids, hcBox.current);
        let new_hc = {
            ...hcBox.current,
            control_devices: new_control_devices,
        };
        const hasBoilerAdapter = HeatingCircuit.isCircuitWithBoilerAdapter(boiler_adapter_ids, new_hc);
        if (HeatingCircuit.isDHW(new_hc)) {
            //Не было адаптера
            if (!wasBoilerAdapter && hasBoilerAdapter) {
                new_hc = {
                    ...HeatingCircuit.setAntilegEnabled(HeatingCircuit.setFlowDHW(new_hc, true), false),
                    //Он тут точно есть - проверяли!
                    dhw_temp_sensor: new_control_devices.find((obj_id) => boiler_adapter_ids.includes(obj_id)),
                };
            }
            //был и не стало
            if (wasBoilerAdapter && !hasBoilerAdapter) {
                new_hc = HeatingCircuit.setFlowDHW(new_hc, false);
            }
        }
        if (hasBoilerAdapter) {
            new_hc = {
                ...new_hc,
                heat_request: HeatingCircuit.HEAT_REQUEST__NO_REQUEST,
            };
        }
        onChange(new_hc);
    }, [onChange, boiler_adapter_ids]);
    const circuitControlDevices = useMemo(() => removeNulls([
        HeatingCircuit.isBoiler(item) || HeatingCircuit.isDHW(item)
            ? boilerAdapters
            : null,
        device_spec.io.has_relay_controls ? relays : null,
        device_spec.io.has_pumps ? pumps : null,
        device_spec.io.has_threeway_taps &&
            (HeatingCircuit.isConsumer(item) || HeatingCircuit.isCooling(item))
            ? threewayTaps
            : null,
        // https://git.microline.ru/zont/server/-/issues/1462
        HeatingCircuit.isBoiler(item) && deviceFirmwareVersion >= 282
            ? analog_outputs
            : null,
    ]), [
        item.type,
        boilerAdapters,
        device_spec.io,
        relays,
        pumps,
        threewayTaps,
        analog_outputs,
    ]);
    const onChangeHeatingCircuitIcon = useCallback((icon_id) => {
        const new_ui_settings = {
            ...device_ui_settings,
            icons: {
                ...device_ui_settings?.icons,
                heating_circuits: {
                    ...device_ui_settings?.icons?.heating_circuits,
                    [id]: icon_id,
                },
                heating_modes: {
                    ...device_ui_settings?.icons?.heating_modes,
                },
            },
        };
        replace_device_ui_settings(new_ui_settings);
    }, [device_ui_settings, id]);
    const heating_circuit_icon = useMemo(() => device_ui_settings?.icons?.heating_circuits?.[id], [device_ui_settings?.icons?.heating_circuits, id]);
    const context = useMemo(() => ({
        z3k_name,
        id,
        item,
        onChange,
        withBoilerAdapter,
        threewayTaps,
    }), [z3k_name, id, item, onChange, withBoilerAdapter, threewayTaps]);
    const multipleSelectionTableOptions = useMemo(() => {
        return [
            {
                title: 'Исполнительные устройства',
                help_id: 'heating_circuits_control_devices',
                options: circuitControlDevices,
                z3k_field: 'control_devices',
            },
        ];
    }, [item]);
    const settingRegisterEditingProps = useEditingProps(id, z3k_name, 'setting_register');
    return (_jsx(HeatingCircuitContext.Provider, { value: context, children: _jsxs(ErrorBoundary, { children: [_jsxs(SettingContainer, { mb: 1, children: [_jsx(Control, { label: "\u041D\u0430\u0437\u0432\u0430\u043D\u0438\u0435", field: TextInputWrapper, help_id: "heating_circuits_name", ...nameEditingProps }), _jsx(IconsLibrary, { value: heating_circuit_icon || 0, onChange: onChangeHeatingCircuitIcon, icons: heating_circuits_icons }), _jsx(Control, { label: "\u0422\u0438\u043F", ...typeEditingProps, field: type_field, onChange: onChangeType, help_id: "heating_circuits_type" }), HeatingCircuit.isConsumer(item) && (_jsx(ControlWithoutValidation, { label: "\u0421\u043F\u043E\u0441\u043E\u0431 \u0442\u0435\u0440\u043C\u043E\u0440\u0435\u0433\u0443\u043B\u0438\u0440\u043E\u0432\u0430\u043D\u0438\u044F", value: HeatingCircuit.getConsumerMode(item), onChange: onChangeConsumerMode, field: consumer_mode_field, help_id: "heating_circuits_thermo_control" })), (HeatingCircuit.isConsumer(item) ||
                            HeatingCircuit.isCooling(item)) &&
                            HeatingCircuit.isAirPIDConsumerMode(item) &&
                            deviceFirmwareVersion >= 339 && (_jsx(FlagSelector, { ...settingRegisterEditingProps, flag_label: "C \u043A\u043E\u043D\u0442\u0440\u043E\u043B\u0435\u043C t\u00B0 \u0422\u041D \u043F\u043E \u043D\u0438\u0436\u043D\u0435\u0439 \u0433\u0440\u0430\u043D\u0438\u0446\u0435 \u043A\u043E\u043D\u0442\u0443\u0440\u0430", flag_help_id: "heating_circuits_no_stop_heating_when_pid_is_in_low_threshold", mask: HeatingCircuit.Flags
                                .NO_STOP_HEATING_WHEN_PID_IS_IN_LOW_THRESHOLD })), HeatingCircuit.isBoiler(item) && (_jsxs(_Fragment, { children: [_jsx(ControlWaterTemperature, {}), controlTurnOffDelay, !withBoilerAdapter &&
                                    external_thermostat_in_lock_mode &&
                                    controlHysteresis] })), HeatingCircuit.isDHW(item) && (_jsxs(_Fragment, { children: [!(withBoilerAdapter && isFlowType) && (_jsxs(SettingCard, { title: "\u0422\u0435\u0440\u043C\u043E\u0434\u0430\u0442\u0447\u0438\u043A \u0442\u0435\u043C\u043F\u0435\u0440\u0430\u0442\u0443\u0440\u044B \u0413\u0412\u0421", children: [_jsx(MaterialObjectSelection, { label: "\u041E\u0441\u043D\u043E\u0432\u043D\u043E\u0439", help_id: "heating_circuits_dhw_temp_sensor", options: wired_radio_boiler_options, ...dhwTempSensorEditingProps }), _jsx(MaterialObjectSelection, { label: "\u0420\u0435\u0437\u0435\u0440\u0432\u043D\u044B\u0439", help_id: "heating_circuits_dhw_temp_sensor_reserve", options: wired_radio_boiler_options, ...dhwTempSensorReserveEditingProps })] })), controlTurnOffDelay, (!withBoilerAdapter ||
                                    (withBoilerAdapter && !isFlowType)) &&
                                    external_thermostat_in_lock_mode &&
                                    controlHysteresis] })), (HeatingCircuit.isCooling(item) ||
                            HeatingCircuit.isConsumer(item) ||
                            item.external_thermostat !== null) && (_jsxs(_Fragment, { children: [!HeatingCircuit.isWaterConsumerMode(item) && (_jsxs(SettingCard, { title: "\u0422\u0435\u0440\u043C\u043E\u0434\u0430\u0442\u0447\u0438\u043A \u0442\u0435\u043C\u043F\u0435\u0440\u0430\u0442\u0443\u0440\u044B \u0432\u043E\u0437\u0434\u0443\u0445\u0430", children: [_jsx(MaterialObjectSelection, { label: "\u041E\u0441\u043D\u043E\u0432\u043D\u043E\u0439", help_id: "heating_circuits_air_temp_sensor", options: wired_temperature_radiosensors_options, ...airTempSensorEditingProps }), _jsx(MaterialObjectSelection, { label: "\u0420\u0435\u0437\u0435\u0440\u0432\u043D\u044B\u0439", help_id: "heating_circuits_air_temp_sensor_reserve", options: wired_temperature_radiosensors_options, ...airTempSensorReserveEditingProps })] })), ((device_spec.heating.has_heating &&
                                    device_spec.heating
                                        .show_water_sensor_when_control_by_air) ||
                                    is_staff) && _jsx(ControlWaterTemperature, {}), service_view && (_jsxs(_Fragment, { children: [controlTurnOffDelay, !withBoilerAdapter &&
                                            external_thermostat_in_lock_mode &&
                                            controlHysteresis] }))] })), _jsx(HeatRequestField, { heating_circuit: item, service_view: service_view, device_spec: device_spec, withBoilerAdapter: withBoilerAdapter, isFlowType: isFlowType, boiler_circuit_boiler_group_options: boiler_circuit_boiler_group_options, heatRequestEditingProps: heatRequestEditingProps, heatSourceEditingProps: heatSourceEditingProps, config: config }), service_view && (_jsx(SettingFullRow, { children: _jsx(MultipleSelectionTable, { elements: multipleSelectionTableOptions, z3k_name: z3k_name, name_second_column: "\u042D\u043B\u0435\u043C\u0435\u043D\u0442\u044B", help_id_second_column: "heating_circuits_elements", notification: false, onChange: onChangeControlDevices }) }))] }), _jsx(HCAdditionalParams, {}), HeatingCircuit.isBoiler(item) && _jsx(CircuitSwitch, { hc: item })] }) }));
});
